import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, TextField, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { saveCustomerContact } from '../services/customerService';
import { getCachedTryOn } from '../services/tryOnCache';

function ContactCapture() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('p');
  const flow = searchParams.get('flow');
  const [contactType, setContactType] = useState('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [preStartStatus, setPreStartStatus] = useState(null); // 'pending' | 'done' | null

  // Watch the pre-started try-on so we can show progress to the user
  useEffect(() => {
    const sessionId = localStorage.getItem('tryOnSessionId');
    if (!sessionId) return;
    let interval;
    const check = () => {
      const entry = getCachedTryOn(sessionId);
      if (!entry) return;
      setPreStartStatus(entry.status);
      if (entry.status !== 'pending') clearInterval(interval);
    };
    check();
    interval = setInterval(check, 800);
    return () => clearInterval(interval);
  }, []);

  const validateEmail = (value) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(value);
  };

  const validatePhone = (value) => {
    // Italian phone: +39 followed by 9-10 digits, or just 3xx... (10 digits)
    const cleaned = value.replace(/\s+/g, '').replace(/-/g, '');
    const re = /^(\+39)?3\d{8,9}$/;
    return re.test(cleaned);
  };

  const handleSubmit = async () => {
    setError('');

    let contactData;

    if (contactType === 'email') {
      if (!email.trim()) {
        setError('Inserisci la tua email');
        return;
      }
      if (!validateEmail(email.trim())) {
        setError('Inserisci un indirizzo email valido');
        return;
      }
      contactData = { type: 'email', value: email.trim() };
    } else {
      if (!phone.trim()) {
        setError('Inserisci il tuo numero di telefono');
        return;
      }
      if (!validatePhone(phone.trim())) {
        setError('Inserisci un numero di telefono italiano valido (es. +39 3xx xxx xxxx)');
        return;
      }
      contactData = { type: 'phone', value: phone.trim() };
    }

    localStorage.setItem('userContact', JSON.stringify(contactData));

    // Save to Firebase for admin CRM
    try {
      const contactHash = await saveCustomerContact(contactData);
      localStorage.setItem('contactHash', contactHash);
    } catch (err) {
      console.error('Error saving contact to Firebase:', err);
    }

    if (flow === 'armocromia') {
      navigate('/armocromia-results');
    } else {
      navigate(`/try-on-result${productId ? `?p=${productId}` : ''}`);
    }
  };

  return (
    <Box
      sx={{
        height: '100dvh',
        background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px 24px',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          sx={{
            ml: 2,
            fontSize: '18px',
            fontWeight: 700,
            color: 'white'
          }}
        >
          I tuoi contatti
        </Typography>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 2,
          gap: { xs: 1.5, sm: 3 },
          overflow: 'auto'
        }}
      >
        {/* Step indicator */}
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            color: 'rgba(255, 255, 255, 0.5)',
            textTransform: 'uppercase',
            letterSpacing: '2px'
          }}
        >
          Ultimo passaggio
        </Typography>

        {/* Title */}
        <Typography
          sx={{
            fontSize: { xs: '28px', sm: '34px' },
            fontWeight: 800,
            color: 'white',
            textAlign: 'center',
            lineHeight: 1.15,
            letterSpacing: '-0.5px',
            mt: -1
          }}
        >
          Dove ti inviamo
          <br />
          <Box
            component="span"
            sx={{
              background: 'linear-gradient(135deg, #9ba8ff, #c8a7ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            i tuoi risultati?
          </Box>
        </Typography>

        {/* Micro-badge */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.65,
            borderRadius: '999px',
            background: 'rgba(155, 168, 255, 0.1)',
            border: '1px solid rgba(155, 168, 255, 0.25)'
          }}
        >
          <Box sx={{ fontSize: '13px' }}>⚡</Box>
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#c8a7ff', letterSpacing: '0.3px' }}>
            Risultati pronti in ~30 secondi
          </Typography>
        </Box>

        {/* Form card */}
        <Box
          sx={{
            width: '100%',
            maxWidth: '400px',
            background: 'rgba(255, 255, 255, 0.04)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '28px',
            p: { xs: 2.5, sm: 3.5 },
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}
        >
          {/* Segmented toggle email/phone */}
          <Box
            sx={{
              display: 'flex',
              position: 'relative',
              background: 'rgba(255, 255, 255, 0.06)',
              borderRadius: '14px',
              p: 0.5,
              mb: 2.5,
              border: '1px solid rgba(255, 255, 255, 0.08)'
            }}
          >
            {/* Animated pill */}
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                left: 4,
                width: 'calc(50% - 4px)',
                height: 'calc(100% - 8px)',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '10px',
                transform: contactType === 'email' ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.35)'
              }}
            />
            {['email', 'phone'].map((val) => {
              const selected = contactType === val;
              const Icon = val === 'email' ? EmailIcon : PhoneIcon;
              return (
                <Box
                  key={val}
                  onClick={() => { setContactType(val); setError(''); }}
                  sx={{
                    flex: 1,
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    py: 1.4,
                    fontSize: '14px',
                    fontWeight: 700,
                    color: selected ? '#fff' : 'rgba(255, 255, 255, 0.8)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'color 0.3s ease'
                  }}
                >
                  <Icon sx={{ fontSize: '18px' }} />
                  {val === 'email' ? 'Email' : 'Telefono'}
                </Box>
              );
            })}
          </Box>

          {/* Input */}
          <TextField
            fullWidth
            placeholder={contactType === 'email' ? 'nome@esempio.com' : '+39 3xx xxx xxxx'}
            type={contactType === 'email' ? 'email' : 'tel'}
            value={contactType === 'email' ? email : phone}
            onChange={(e) => {
              if (contactType === 'email') setEmail(e.target.value);
              else setPhone(e.target.value);
              setError('');
            }}
            error={!!error}
            helperText={error}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1.2, display: 'flex', color: 'rgba(255,255,255,0.5)' }}>
                  {contactType === 'email'
                    ? <EmailIcon sx={{ fontSize: '18px' }} />
                    : <PhoneIcon sx={{ fontSize: '18px' }} />
                  }
                </Box>
              )
            }}
            sx={{
              mb: 2.5,
              '& .MuiOutlinedInput-root': {
                height: '46px',
                borderRadius: '14px',
                fontSize: '15px',
                color: '#fff',
                background: 'rgba(255, 255, 255, 0.04)',
                transition: 'all 0.3s ease',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.12)',
                  borderWidth: '1.5px'
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.25)'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#9ba8ff',
                  borderWidth: '1.5px'
                },
                '&.Mui-focused': {
                  background: 'rgba(255, 255, 255, 0.07)'
                }
              },
              '& .MuiOutlinedInput-input': {
                py: 0,
                height: '100%',
                '&::placeholder': { color: 'rgba(255,255,255,0.35)', opacity: 1 }
              },
              '& .MuiFormHelperText-root': {
                color: '#ff8a8a',
                ml: 1.5,
                fontSize: '12px',
                fontWeight: 500
              }
            }}
          />

          {/* Submit */}
          <Button
            fullWidth
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={handleSubmit}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              py: 1.8,
              fontSize: '16px',
              fontWeight: 700,
              borderRadius: '14px',
              textTransform: 'none',
              boxShadow: '0 10px 30px rgba(102, 126, 234, 0.45)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                boxShadow: '0 14px 40px rgba(102, 126, 234, 0.6)',
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease'
              }
            }}
          >
            Scopri il tuo look
          </Button>

          {/* Privacy disclaimer */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 2, justifyContent: 'center' }}>
            <Box sx={{ fontSize: '11px' }}>🔒</Box>
            <Typography
              sx={{
                textAlign: 'center',
                fontSize: '11px',
                color: 'rgba(255,255,255,0.45)',
                fontWeight: 500
              }}
            >
              Non condivideremo i tuoi dati con terzi
            </Typography>
          </Box>

          {preStartStatus && (
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: '999px',
                background: preStartStatus === 'done'
                  ? 'rgba(34, 197, 94, 0.12)'
                  : 'rgba(102, 126, 234, 0.12)',
                border: preStartStatus === 'done'
                  ? '1px solid rgba(34, 197, 94, 0.35)'
                  : '1px solid rgba(102, 126, 234, 0.35)',
                animation: 'fadeInUp 0.4s ease-out',
                '@keyframes fadeInUp': {
                  from: { opacity: 0, transform: 'translateY(8px)' },
                  to: { opacity: 1, transform: 'translateY(0)' }
                }
              }}
            >
              {preStartStatus === 'pending' ? (
                <>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#9ba8ff',
                      animation: 'pulseDot 1.4s ease-in-out infinite',
                      '@keyframes pulseDot': {
                        '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                        '50%': { opacity: 0.4, transform: 'scale(0.7)' }
                      }
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.85)',
                      fontWeight: 600,
                      backgroundImage: 'linear-gradient(90deg, rgba(255,255,255,0.85) 0%, #c8a7ff 50%, rgba(255,255,255,0.85) 100%)',
                      backgroundSize: '200% 100%',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      animation: 'shimmer 2.5s linear infinite',
                      '@keyframes shimmer': {
                        '0%': { backgroundPosition: '200% 0' },
                        '100%': { backgroundPosition: '-200% 0' }
                      }
                    }}
                  >
                    Stiamo già preparando il tuo look…
                  </Typography>
                </>
              ) : preStartStatus === 'done' ? (
                <>
                  <Box sx={{ fontSize: '13px' }}>✨</Box>
                  <Typography sx={{ fontSize: '12px', color: '#86efac', fontWeight: 700 }}>
                    Il tuo look è pronto!
                  </Typography>
                </>
              ) : null}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default ContactCapture;
